import { useState, useEffect, useCallback } from 'react';
import { getPaymentImages, deletePaymentImage } from '../api/paymentImages';
import type { ImageDto } from '@/components/shared/types';

/**
 * Hook for fetching and managing payment images
 */
export function usePaymentImages(jobId: number, paymentId: number) {
  const [images, setImages] = useState<ImageDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchImages = useCallback(async () => {
    if (!paymentId) {
      setImages([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const paymentImages = await getPaymentImages(jobId, paymentId);
      setImages(paymentImages);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to fetch payment images');
      setImages([]);
    } finally {
      setIsLoading(false);
    }
  }, [jobId, paymentId]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const deleteImage = async (imageId: number): Promise<boolean> => {
    try {
      await deletePaymentImage(jobId, paymentId, imageId);
      setImages((prev) => prev.filter((img) => img.id !== imageId));
      return true;
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to delete image');
      return false;
    }
  };

  return {
    images,
    isLoading,
    error,
    refetch: fetchImages,
    deleteImage,
  };
}
